/**
 * chartViewSync — Sincroniza la vista de varias gráficas hermanas.
 *
 * QUÉ HACE: expone un contexto **opcional** y un hook para que varias gráficas
 *   de la misma familia (por ejemplo, la gráfica principal de una tarjeta y la
 *   que se incrusta en la respuesta del Copilot) compartan la vista activa
 *   (Barras, Línea, Dona, Tabla…). Sin provider, cada gráfica usa su estado
 *   local como siempre; con provider, el cambio de vista de una se refleja en
 *   las demás. La vista compartida se valida contra las vistas válidas de cada
 *   gráfica (`validIds`), de modo que un id ajeno se ignora sin romper.
 * QUÉ INVOCA: React (createContext/useContext) y el catálogo `ChartViewId`.
 * QUÉ EXPORTA: ChartViewSyncValue, ChartViewSyncContext,
 *   STANDARD_CHART_VIEW_IDS, isChartViewId, useChartViewSync.
 * QUÉ NO DEBE HACER: no conoce datos, tarjetas, servicios ni persistencia.
 */
import { createContext, useContext } from "react";

/** Valor del contexto de sincronización de vistas. */
export interface ChartViewSyncValue {
  /** Vista activa compartida (null = cada gráfica usa la suya). */
  view: string | null;
  /** Publica una vista nueva en todas las gráficas del grupo. */
  setView: (view: string) => void;
}

/** Contexto opcional: `null` cuando no se sincroniza ninguna gráfica. */
export const ChartViewSyncContext = createContext<ChartViewSyncValue | null>(
  null,
);

/**
 * Vistas del orden estándar de Métricas (las 9 que comparten Horas Pico,
 * Gastos por día, Tendencia, Ventas por día, Calendario, Métodos de pago y
 * Punto de equilibrio).
 */
export type StandardChartViewId =
  | "bar"
  | "barHorizontal"
  | "line"
  | "velas"
  | "matriz"
  | "radar"
  | "donut"
  | "treemap"
  | "tabla";

export const STANDARD_CHART_VIEW_IDS: readonly StandardChartViewId[] = [
  "bar",
  "barHorizontal",
  "line",
  "velas",
  "matriz",
  "radar",
  "donut",
  "treemap",
  "tabla",
];

/** Type guard: `value` es una de las vistas válidas de la gráfica. */
export const isChartViewId = <T extends string>(
  value: string,
  ids: readonly T[],
): value is T => ids.some((id) => id === value);

/**
 * Vista efectiva + setter de una gráfica. Con provider, la vista compartida
 * manda (validada contra `validIds`) y el setter actualiza el estado local y el
 * compartido; sin provider, se comporta como un `useState` normal.
 */
export const useChartViewSync = <T extends string>(
  localView: T,
  setLocalView: (view: T) => void,
  validIds: readonly T[],
): [T, (view: T) => void] => {
  const sync = useContext(ChartViewSyncContext);
  if (!sync) return [localView, setLocalView];
  const effective =
    sync.view !== null && isChartViewId(sync.view, validIds)
      ? sync.view
      : localView;
  const setView = (next: T) => {
    setLocalView(next);
    sync.setView(next);
  };
  return [effective, setView];
};
